import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { OrgOnlineAdmissionsApi } from '@/services/org-online-admissions.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Input, Select } from '@/components/ui/Input';

/**
 * Admissions Summary & Analytics — Org tier. Per-school total/pending/
 * admitted counts (all-time, unfiltered — an already-built org feature
 * independent of the real state report), plus a year-scoped per-grade
 * breakdown matching the real `state-reports.student-admission-report`
 * shape (per-school rows x per-grade count columns, selectable calendar
 * year). Grade COLUMNS are dynamic (this project's real classes.name
 * values via a proper class_id FK), not real legacy's fixed Pre-School
 * through Grade 6 string-matching bucket hack.
 */
export function OrgOnlineAdmissionsSummaryPage() {
  const [exporting, setExporting] = useState<'csv' | 'xlsx' | null>(null);
  const [year, setYear] = useState<number | undefined>(undefined);
  const [state, setState] = useState('');
  const [search, setSearch] = useState('');

  const { data: filters } = useQuery({
    queryKey: ['org-online-admissions-filters'],
    queryFn: () => OrgOnlineAdmissionsApi.filters(),
  });

  const { data, isFetching } = useQuery({
    queryKey: ['org-online-admissions-summary', year, state],
    queryFn: () => OrgOnlineAdmissionsApi.summary(year, state || undefined),
  });
  const allRows = data?.rows ?? [];
  const years = data?.years ?? [];
  const selectedYear = year ?? data?.selectedYear;

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? allRows.filter((r) => r.schoolName.toLowerCase().includes(q)) : allRows;
  }, [allRows, search]);

  const gradeColumns = [...new Set(rows.flatMap((r) => r.grades.map((g) => g.className)))].sort();

  const totals = rows.reduce(
    (acc, r) => ({ total: acc.total + r.total, pending: acc.pending + r.pending, admitted: acc.admitted + r.admitted }),
    { total: 0, pending: 0, admitted: 0 },
  );

  const runExport = async (format: 'csv' | 'xlsx') => {
    setExporting(format);
    try {
      await OrgOnlineAdmissionsApi.export(format);
    } finally {
      setExporting(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Online Admission — Summary & Analytics"
        description="Applications, pending, and admitted counts per school, plus a per-grade breakdown for the selected year."
        actions={
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
            <button
              className="px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('xlsx')}
              disabled={!!exporting}
            >
              {exporting === 'xlsx' ? 'Exporting…' : 'Export Excel'}
            </button>
            <div className="w-px bg-slate-200" />
            <button
              className="px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('csv')}
              disabled={!!exporting}
            >
              {exporting === 'csv' ? 'Exporting…' : 'Export CSV'}
            </button>
          </div>
        }
      />

      <div className="mb-4 grid grid-cols-3 gap-4">
        <div className="card p-4">
          <div className="text-xs uppercase tracking-wide text-slate-400">Total Applications</div>
          <div className="mt-1 text-2xl font-semibold text-slate-900">{totals.total}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs uppercase tracking-wide text-slate-400">Pending</div>
          <div className="mt-1 text-2xl font-semibold text-amber-600">{totals.pending}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs uppercase tracking-wide text-slate-400">Admitted</div>
          <div className="mt-1 text-2xl font-semibold text-green-600">{totals.admitted}</div>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Grade breakdown year:</span>
          <Select className="!w-32" value={selectedYear ?? ''} onChange={(e) => setYear(e.target.value ? Number(e.target.value) : undefined)}>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">State:</span>
          <Select className="!w-40" value={state} onChange={(e) => setState(e.target.value)}>
            <option value="">All states</option>
            {(filters?.stateNames ?? []).map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Select>
        </div>
        <div className="relative sm:w-64">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search school" className="pl-9" />
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">School</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-slate-500">Total</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-slate-500">Pending</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-slate-500">Admitted</th>
                {gradeColumns.map((g) => (
                  <th key={g} className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-slate-500">
                    {g}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isFetching && rows.length === 0 ? (
                <tr>
                  <td colSpan={4 + gradeColumns.length} className="px-4 py-8 text-center text-slate-400">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={4 + gradeColumns.length} className="px-4 py-8 text-center text-slate-500">
                    {search.trim() ? `No schools match "${search}".` : 'No schools found.'}
                  </td>
                </tr>
              ) : (
                rows.map((r) => {
                  const gradeCountByName = new Map(r.grades.map((g) => [g.className, g.count]));
                  return (
                    <tr key={r.schoolId}>
                      <td className="px-4 py-3 font-medium text-slate-900">{r.schoolName}</td>
                      <td className="px-4 py-3 text-right text-slate-700">{r.total}</td>
                      <td className="px-4 py-3 text-right text-amber-600">{r.pending}</td>
                      <td className="px-4 py-3 text-right text-green-600">{r.admitted}</td>
                      {gradeColumns.map((g) => (
                        <td key={g} className="px-4 py-3 text-right text-slate-700">
                          {gradeCountByName.get(g) ?? 0}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
